// src/components/Onboarding/IntroHero.js
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import profileMashup from '../../images/profileMashup.png';

export default function IntroHero({
  imageSrc, // trustpilotBanner
  title = 'We can help.',
  description = 'We’ve helped 1,000+ people in their hair restoration journey so far.',
  theme = 'dark',
  mode = 'splash',

  // Compact view props
  compactStatText = '1,000+ people',
  compactIconSrc = profileMashup,
  compactIconClass = 'h-20 w-20',
  compactHeroSizeClass = 'h-36 w-36 md:h-48 md:w-48', // final size at identity (scale 1)

  // Animation tuning
  durationMs = 2000,
  easing = 'cubic-bezier(0.22, 1, 0.36, 1)',
  // NEW: notify parent when compact animation fully ends (incl. reduced-motion)
  onCompactEnd,
  // NEW: control when the compact “Online stat” is shown
  showCompactStat = true,

  // NEW: external skip trigger (increment to force-complete current phase)
  forceSkip,
}) {
  const isDark = theme === 'dark';

  // Keep splash layer visible until the transition finishes
  const [showSplash, setShowSplash] = useState(mode === 'splash');
  const [isAnimating, setIsAnimating] = useState(false);

  const splashImgRef = useRef(null);      // big centered image (splash)
  const splashLayerRef = useRef(null);    // whole splash overlay
  const compactImgRef = useRef(null);     // small final image (compact)

  // Ensure compact block is always mounted so we can measure the target rect
  const shouldShowCompactBlock = true;

  // Respond to external mode changes
  useEffect(() => {
    if (mode === 'splash') {
      // Reset to splash state
      setShowSplash(true);
      setIsAnimating(false);
      // Clear any transforms if left over
      const img = compactImgRef.current;
      if (img) {
        img.style.transition = '';
        img.style.transform = '';
        img.style.opacity = '0'; // compact stays hidden during splash
      }
      const splashLayer = splashLayerRef.current;
      if (splashLayer) splashLayer.style.opacity = '1';
    } else {
      // Kick off the animation when entering compact
      setIsAnimating(true);
    }
  }, [mode]);

  // Perform the FLIP animation: single node, inverse transform → identity
  useLayoutEffect(() => {
    if (!isAnimating) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const splashImg = splashImgRef.current;
    const compactImg = compactImgRef.current;
    const splashLayer = splashLayerRef.current;

    if (!splashImg || !compactImg) return;

    // Make sure the compact image is measurable & visible for the animation
    compactImg.style.opacity = '1';
    compactImg.style.willChange = 'transform';
    compactImg.style.transformOrigin = 'top left';
    compactImg.style.transition = 'none';

    // Measure rects after layout
    const from = splashImg.getBoundingClientRect();
    const to = compactImg.getBoundingClientRect();

    // Compute the inverse transform (from -> to)
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    const sx = from.width / to.width || 1;
    const sy = from.height / to.height || 1;

    if (reduceMotion) {
      // Skip animation for reduced-motion users
      compactImg.style.transform = '';
      if (splashLayer) splashLayer.style.opacity = '0';
      setShowSplash(false);
      setIsAnimating(false);
      // notify parent immediately in reduced motion
      onCompactEnd && onCompactEnd();
      return;
    }

    // Prime: place compact exactly over the splash image visually
    // If transform is effectively identity, nudge slightly so a transition actually occurs.
    const EPS = 0.001;
    const _dx = Math.abs(dx) < EPS ? EPS : dx;
    const _dy = Math.abs(dy) < EPS ? EPS : dy;
    const _sx = Math.abs(sx - 1) < EPS ? 1 + EPS : sx;
    const _sy = Math.abs(sy - 1) < EPS ? 1 + EPS : sy;
    compactImg.style.transform = `translate(${_dx}px, ${_dy}px) scale(${_sx}, ${_sy})`;

    // Force reflow so the browser applies the primed transform immediately
    compactImg.getBoundingClientRect();

    // Animate to identity; fade splash
requestAnimationFrame(() => {
  compactImg.style.transition = `transform ${durationMs}ms ${easing}`;
  compactImg.style.transform = 'translate(0px, 0px) scale(1, 1)';

  // Remove slow fade-out: hide splash immediately without opacity animation
  if (splashLayer) {
    splashLayer.style.transition = '';
    splashLayer.style.opacity = '0';
  }

  let ended = false;
  const cleanup = () => {
    compactImg.style.transition = '';
    compactImg.style.willChange = '';
    if (splashLayer) splashLayer.style.transition = '';
    compactImg.removeEventListener('transitionend', onCompactEndLocal);
  };
  const onCompactEndLocal = () => {
    if (ended) return;
    ended = true;
    setShowSplash(false);
    setIsAnimating(false);
    cleanup();
    onCompactEnd && onCompactEnd();
  };

  compactImg.addEventListener('transitionend', onCompactEndLocal);
});

   }, [isAnimating, durationMs, easing]);

  // External skip: immediately finish to compact end state
  useEffect(() => {
    // Only act when a positive tick is provided
    if (typeof forceSkip !== 'number' || forceSkip <= 0) return;

    const splashLayer = splashLayerRef.current;
    const compactImg = compactImgRef.current;

    // Ensure compact image is visible and at final transform
    if (compactImg) {
      compactImg.style.transition = '';
      compactImg.style.opacity = '1';
      compactImg.style.transform = 'translate(0px, 0px) scale(1, 1)';
    }
    if (splashLayer) {
      splashLayer.style.transition = '';
      splashLayer.style.opacity = '0';
    }

    setShowSplash(false);
    setIsAnimating(false);
    onCompactEnd && onCompactEnd();
  }, [forceSkip, onCompactEnd]);

  return (
    <div className="relative">
      {/* Compact header (ALWAYS MOUNTED for measuring the target rect) */}
      <div
        className={[
          isDark ? 'text-white' : 'text-gray-900',
          // Don't block interactions after splash is gone
          showSplash ? 'pointer-events-none' : 'pointer-events-auto',
        ].join(' ')}
      >
        {shouldShowCompactBlock && (
          <div className="flex flex-col items-center justify-center">
            {/* FINAL hero (target). Hidden during splash but measurable. */}
            <img
              ref={compactImgRef}
              src={imageSrc}
              alt=""
              className={`${compactHeroSizeClass} rounded-xl object-contain`}
              style={{ opacity: showSplash ? 0 : 1 }}
            />

            {/* One-line stat: profileMashup + text */}
            {showCompactStat && (
              <div className="flex items-center justify-center gap-6 pb-5 introStatFade">
                <img
                  src={compactIconSrc}
                  alt=""
                  className={`${compactIconClass} object-contain h-fit`}
                />
                <span className={`text-base font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {compactStatText}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Splash overlay (initial center view) */}
      {showSplash && (
        <div
          ref={splashLayerRef}
          className={[
            'fixed inset-0 z-20 flex items-center justify-center px-6',
            isDark ? 'bg-mm_black text-white' : 'bg-white text-gray-900',
          ].join(' ')}
          role="status"
          aria-live="polite"
        >
          <div className="w-full max-w-md flex flex-col items-center text-center gap-4 ">
            <img
              ref={splashImgRef}
              src={imageSrc}
              alt=""
              className="h-64 w-64 rounded-2xl object-contain"
              style={{ animation: 'subtleFloat 3s ease-in-out infinite' }}
            />
            <div className="min-w-0 space-y-4">
              <h2 className="text-3xl font-bold leading-tight px-2">{title}</h2>
              <p className="text-lg leading-relaxed px-6 max-w-md ">{description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Keyframes for subtle float in splash */}
      <style>{`
        @keyframes subtleFloat {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
          100% { transform: translateY(0px); }
        }
        @keyframes fadeOpacityUp {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .introStatFade {
          opacity: 0;
          animation: fadeOpacityUp 600ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .introStatFade { animation: fadeOpacityUp 1ms linear forwards !important; }
        }
      `}</style>
    </div>
  );
}
